import moment from 'moment';
import { US_CONFIG, GB_CONFIG } from '../config/country-configs';
import {
  SUBSCRIPTION_TYPE,
  REACT_APP_STRIPE_CHARGE_PERCENT,
  REACT_APP_STRIPE_CHARGE_FIXED_CENTS,
  REACT_APP_CREDIT_CHARGE_PERCENT,
  REACT_APP_CREDIT_CHARGE_FIXED_CENTS,
  REACT_APP_DEBIT_CHARGE_PERCENT,
  REACT_APP_DEBIT_CHARGE_FIXED_CENTS,
  ZIPCODE_PATTERN,
  EMAIL_PATTERN,
} from 'global-constants/constants';
import * as Roles from 'global-constants/roles';
import {
  SUBSCRIBILI_USER,
  SUPER_ADMIN,
  PARTNER_ADMIN,
  REVSHARE_ADMIN,
  PARTNER_USER,
  GROUP_ADMIN,
  GROUP_USER,
  REGION_ADMIN,
  REGION_USER,
  LOCATION_ADMIN,
  LOCAL_USER,
} from 'global-constants/roles';
import {
  ALL_PARTNERS,
  ALL_GROUPS,
  ALL_LOCATIONS,
  ALL_REGIONS,
  STRIPE_ERRORS_MESSAGES,
  STRIPE_ERRORS,
} from 'Views/Common/constants';
import _ from 'lodash';
import { isWebUri } from 'valid-url';
import { formatToDateString, formatToMilliSeconds } from 'utils';
import {
  DENTAL_INDUSTRIES,
  PAYMENT_TYPES_NAME,
  PLAN_PROCESS_FREQUENCIES,
  ROLES_LIST,
  STRIPE_CONTACT_MIN_AGE,
} from 'Views/Common/enum';
import parsePhoneNumber from 'libphonenumber-js';
import { centsToDollars } from 'Views/FlexPayV2/utils/commonUtils';

export const validateEmail = (email) => EMAIL_PATTERN.test(email);

const validateMobile = (phone, countryCode = 'US') => {
  if (!phone) return false;

  const cleaned = phone.replaceAll(/\D/g, '');

  if (countryCode === 'GB') {
    // Starts with UK country code 44
    if (cleaned.startsWith('44')) {
      // Invalid if there are two zeros immediately after 44
      if (cleaned[2] === '0' && cleaned[3] === '0') {
        return false;
      }

      // If the first digit after 44 is 0 → allow 13 digits (+44 + leading 0 + 10 digits)
      if (cleaned.length >= 3 && cleaned[2] === '0') {
        return cleaned.length === 13;
      } else {
        // Otherwise, allow 12 digits (+44 + 10 digits)
        return cleaned.length === 12;
      }
    }

    // Local UK numbers starting with 0 (fallback)
    // Allow 12–13 digits
    return cleaned.length >= 12 && cleaned.length <= 13;
  } else if (countryCode === 'US') {
    if (cleaned.startsWith('1')) return cleaned.length === 11;
    return cleaned.length === 10;
  } else {
    // Default for other countries, e.g., US
    return /^\d{11}$/.test(cleaned);
  }
};

const validateWebsite = (website) => isWebUri(website);

export const validateZipCode = (zipcode, countryConfig = {}) => {
  return countryConfig?.address?.postalCodePattern?.test(zipcode);
};

const validateFourDigitSSN = (ssn_last_4) => Boolean(ssn_last_4?.length === 4);

const validateNPI = (value) => /^\d{10}$/.test(value);

const validateTaxId = (value) => /^\d{9}$/.test(value);

const validateFacilityId = (value) => /^[a-zA-Z0-9]{3,8}$/.test(value);

const validateSlug = (value) =>
  value.length <= 64 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

const validateName = (value) => /^[\p{L}0-9-'. ]+$/u.test(value);

export const isValidDate = (d) => d instanceof Date && !isNaN(d);

export const getSelectedGroupIds = (group_ids = [], canSelectMultipleGroups = false) =>
  canSelectMultipleGroups ? group_ids.filter((id) => id !== 'ALL_GROUPS') : [];

export const getQueryString = (data) => {
  let queryStr = '';
  let count = 0;
  for (var key in data) {
    if (data[key]) {
      queryStr += `${count > 0 ? '&' : ''}${key}=${data[key]}`;
      count++;
    }
  }
  return queryStr;
};

// subscriber config starts

export const getKeysName = (name) => {
  if (name === 'group_id') {
    return 'Group';
  } else if (name === 'location_id') {
    return 'Location';
  } else if (name === 'facility_id') {
    return 'Facility';
  } else if (name === 'partner_id') {
    return 'Partner';
  } else if (name === 'region_id') {
    return 'Region';
  } else if (name === 'plan_package_id') {
    return 'Plan Name';
  } else if (name === 'plan_id') {
    return 'Plan Frequency';
  } else if (name === 'gender') {
    return 'Gender';
  } else if (name === 'biological_sex') {
    return 'Biological Gender';
  } else if (name === 'date_of_birth') {
    return 'Date of Birth';
  } else if (name === 'first_name') {
    return 'First Name';
  } else if (name === 'last_name') {
    return 'Last Name';
  } else if (name === 'full_name') {
    return 'Full Name';
  } else if (name === 'relationship') {
    return 'Relationship';
  } else {
    return name;
  }
};

const checkAllLevels = (levelId) =>
  levelId === ALL_PARTNERS.partner_id ||
  levelId === ALL_GROUPS.group_id ||
  levelId === ALL_REGIONS.region_id ||
  levelId === ALL_LOCATIONS.location_id;

const membersConfig = () => ({
  first_name: '',
  last_name: '',
  date_of_birth: null,
  gender: '',
  errorCheck: {
    first_name: { ...defaultFieldState },
    last_name: { ...defaultFieldState },
    date_of_birth: { ...defaultFieldState },
    gender: { ...defaultFieldState },
  },
});

export const getPartnerId = (groupList, group_id) => {
  for (let i in groupList) {
    let indGroup = groupList[i];
    if (indGroup.group_id === group_id) {
      return indGroup.partner_id;
    }
  }
  return null;
};

export const defaultFieldState = {
  required: true,
  isValid: true,
  value: '',
  text: '',
  error: false,
  touched: false,
};

const getEmployeeReqFields = () => ({
  group_id: { ...defaultFieldState },
  location_id: { ...defaultFieldState },
  // "partner_id": { required: true, isValid: true, value: '', text: '', error: false, touched: false },
  plan_package_id: { ...defaultFieldState },
  plan_id: { ...defaultFieldState },
  patient_name: {
    first_name: { ...defaultFieldState },
    last_name: { ...defaultFieldState },
  },
  date_of_birth: { ...defaultFieldState },
  gender: { ...defaultFieldState },
  email: { ...defaultFieldState },
  phone: { ...defaultFieldState },
  address: {
    line1: { ...defaultFieldState },
    city: { ...defaultFieldState },
    state: { ...defaultFieldState },
    zipcode: { ...defaultFieldState },
  },
  pms_id: { ...defaultFieldState, required: false },
  // emp_id: { ...defaultFieldState },
  // department: { ...defaultFieldState },
  // ben_start_date: { ...defaultFieldState },
});

export const getSubscriberReqFields = (countryConfig = US_CONFIG) => {
  const addressFields = {
    line1: { ...defaultFieldState },
    city: { ...defaultFieldState },
    zipcode: { ...defaultFieldState },
  };

  // Only include state field if the country requires it
  if (countryConfig?.address?.hasStateField !== false) {
    addressFields.state = { ...defaultFieldState };
  }

  return {
    group_id: { ...defaultFieldState },
    location_id: { ...defaultFieldState },
    // "partner_id": { required: true, isValid: true, value: '', text: '', error: false, touched: false },
    plan_package_id: { ...defaultFieldState },
    plan_id: { ...defaultFieldState },
    patient_name: {
      first_name: { ...defaultFieldState },
      last_name: { ...defaultFieldState },
    },
    date_of_birth: { ...defaultFieldState },
    gender: { ...defaultFieldState },
    ...(countryConfig?.code === 'GB'
      ? { biological_sex: { ...defaultFieldState } }
      : {}),
    email: { ...defaultFieldState },
    phone: { ...defaultFieldState },
    address: addressFields,
    pms_id: { ...defaultFieldState, required: false },
    start_date: { ...defaultFieldState, value: moment() },
  };
};

export const responsibleReqFields = (countryConfig) => {
  const addressFields = {
    line1: { ...defaultFieldState },
    city: { ...defaultFieldState },
    zipcode: { ...defaultFieldState },
    country_code: countryConfig?.code,
  };

  // Add state field only if country requires it (default to true for backward compatibility)
  if (!countryConfig || countryConfig?.address?.hasStateField !== false) {
    addressFields.state = { ...defaultFieldState };
  }

  return {
    full_name: { ...defaultFieldState },
    email: { ...defaultFieldState },
    phone: { ...defaultFieldState },
    date_of_birth: { ...defaultFieldState },
    relationship: { ...defaultFieldState },
    address: addressFields,
  };
};

export const addressFieldsBlurred = () => ({
  line1: false,
  line2: true,
  city: false,
  state: false,
  zipcode: false,
});

export const subscriberValues = (countryConfig) => ({
  partner_id: '',
  group_id: '',
  location_id: '',
  region_id: '',
  plan_package_id: '',
  addons: [],
  plan_id: '',
  plan_frequency: '',
  include_signup: true,
  addons_signup: {},
  patient_name: {
    first_name: '',
    last_name: '',
  },
  date_of_birth: null,
  gender: '',
  biological_sex: '',
  email: '',
  phone: '',
  address: {
    line1: '',
    city: '',
    state: '',
    zipcode: '',
    country_code: countryConfig?.code,
  },
  payment_method_id: '',
  pms_id: '',
  pms_display_id: '',
  start_date: moment(),
});

const employeeValues = () => ({
  partner_id: '',
  group_id: '',
  location_id: '',
  region_id: '',
  plan_package_id: '',
  addons: [],
  plan_id: '',
  plan_frequency: '',
  include_signup: true,
  addons_signup: {},
  patient_name: {
    first_name: '',
    last_name: '',
  },
  date_of_birth: null,
  gender: '',
  email: '',
  phone: '',
  address: {
    line1: '',
    city: '',
    state: '',
    zipcode: '',
  },
  payment_method_id: '',
  pms_id: '',
  pms_display_id: '',
  // emp_id: "",
  // department: "",
  // ben_start_date: "",
});

export const responsibleValues = (countryConfig) => {
  const addressValues = {
    line1: '',
    city: '',
    zipcode: '',
    country_code: countryConfig?.code,
  };

  // Add state field only if country requires it
  if (!countryConfig || countryConfig?.address?.hasStateField !== false) {
    addressValues.state = '';
  }

  return {
    full_name: '',
    email: '',
    relationship: '',
    phone: '',
    date_of_birth: null,
    address: addressValues,
  };
};

const filterPlanList = (planList) => {
  const keysArr = [];
  const finalArr = [];

  for (let i in planList) {
    if (!keysArr.length) {
      keysArr.push(planList[i].stripe_product_id);
      finalArr.push(planList[i]);
    } else {
      if (keysArr.indexOf(planList[i].stripe_product_id) === -1) {
        keysArr.push(planList[i].stripe_product_id);
        finalArr.push(planList[i]);
      }
    }
  }

  return finalArr;
};

export const getFinalPlanList = (finalPlanList, stripId) =>
  finalPlanList.filter((plan) => plan.stripe_product_id === stripId);

export const _calculateAge = (birthday) => {
  if (moment(birthday).isValid) {
    return moment().diff(birthday, 'years');
  }
  return null;
};

export const getDateError = (
  value,
  allowed_age_max,
  allowed_age_min,
  type = 'Subscriber',
) => {
  const validDate = moment(value).isValid();
  const futureDate = moment(value).isAfter();
  const exceedsMaxAge = allowed_age_max
    ? calculatePreciseAge(value).years > allowed_age_max
    : false;
  const belowMinAge = allowed_age_min
    ? moment(value).isAfter(moment().subtract(allowed_age_min, 'years'))
    : false;

  const invalidDate = moment(value).isBefore(moment('01-01-1900'));

  let message = '';
  if (!value) message = 'DOB is required';
  else if (!validDate) message = 'Invalid date format';
  else if (futureDate) message = 'Date of Birth cannot be in the future';
  else if (exceedsMaxAge)
    message = `${type}’s age must be ${allowed_age_max} or below for this plan`;
  else if (belowMinAge)
    message = `${type}’s age must be ${allowed_age_min} or above for this plan`;
  else if (invalidDate) message = 'Please enter valid date';

  return message;
};

export const getMaxMemberErrorMessage = (maxMembers) =>
  `Adding more than ${maxMembers} additional members is not allowed in this plan`;

export const calculatePreciseAge = (value) => {
  const birthDate = moment(value);
  const today = moment();

  const years = today.diff(birthDate, 'years');
  birthDate.add(years, 'years');

  const months = today.diff(birthDate, 'months');
  birthDate.add(months, 'months');

  const days = today.diff(birthDate, 'days');
  return { years, months, days };
};

export const validateStartDate = (
  value,
  allow_future_dated = false,
  frequency = 'annual_billed_monthly',
) => {
  let message = '';

  const validDate = moment(value, 'MM/DD/YYYY', true).isValid();
  const monthlyplan = frequency === 'annual_billed_monthly';

  if (!value) {
    message = 'Start Date is required';
  } else if (!validDate) {
    message = 'Invalid date format';
  } else {
    const oneMonthBefore = moment().subtract(1, 'month').add(1, 'day');
    const oneYearBefore = moment().subtract(1, 'year').add(1, 'day');
    const futureDate = moment(value).isAfter();
    const oneYearLater = moment().add(1, 'year');

    if (
      monthlyplan &&
      moment(value, 'MM/DD/YYYY').isBefore(oneMonthBefore, 'day')
    ) {
      message = 'Start Date cannot be more than 1 month in the past';
    } else if (
      !monthlyplan &&
      moment(value, 'MM/DD/YYYY').isBefore(oneYearBefore, 'day')
    ) {
      message = 'Start Date cannot be more than 1 year in the past';
    } else if (!allow_future_dated && futureDate) {
      message = 'Start Date cannot be in the future for this plan';
    } else if (
      allow_future_dated &&
      moment(value, 'MM/DD/YYYY').isAfter(oneYearLater, 'day')
    ) {
      message = 'Start Date cannot be more than 1 year in the future';
    }
  }

  return message;
};

export const getStripeDateError = (value) => {
  let dateError = getDateError(value);

  if (!dateError) {
    const dobDate = moment(value);
    const maxDate = moment().subtract(STRIPE_CONTACT_MIN_AGE, 'years');

    if (dobDate > maxDate)
      dateError = `Business representative cannot be below ${STRIPE_CONTACT_MIN_AGE} years`;
  }

  return dateError;
};

export const getTargetValue = ({ target: { type, value } }) => {
  if (type === 'number') {
    let parsedValue = Math.abs(parseFloat(+value.replace(/\D/gi, '')));

    return isNaN(parsedValue) ? 0 : parsedValue;
  }

  return value;
};

const prepareDependentsPayload = (
  additionMembers,
  planFrequency,
  addonsList,
  countryConfig = {},
) => {
  let dependentsPayload = [];
  if (additionMembers && additionMembers.length) {
    dependentsPayload = additionMembers.map((member) => {
      let addonPlans = [];
      if (member?.addons?.length) {
        member?.addons.forEach((indAddon) => {
          const planDetail = Utils.getPlanId(
            indAddon,
            'Dependent',
            planFrequency,
            addonsList,
          );
          const { plan_id } = planDetail || {};

          plan_id &&
            addonPlans.push({
              plan_id,
              include_signup: member?.addons_signup?.[indAddon],
            });
        });
      }

      const memberDetails = {
        plans: [
          {
            plan_id: member?.plan_id,
            include_signup: member?.include_signup,
          },
          ...addonPlans,
        ],
        patient_name: {
          first_name: member.first_name,
          last_name: member.last_name,
        },
        date_of_birth: formatToDateString(member.date_of_birth),
        gender: member.gender,
      };

      if (countryConfig?.biologicalSexRequired) {
        memberDetails['biological_sex'] = member?.biological_sex || member.gender;
      }

      if (member?.relationship) {
        memberDetails['relationship'] = member?.relationship;
      }
      if (member?.pms_id) {
        memberDetails['pms_id'] = member?.pms_id;
      }
      if (member?.pms_display_id) {
        memberDetails['pms_display_id'] = member?.pms_display_id;
      }

      return memberDetails;
    });
  }

  return dependentsPayload;
};

export const checkIfExternalPayment = (pType) =>
  pType === PAYMENT_TYPES_NAME?.Cash || pType === PAYMENT_TYPES_NAME?.Financed;

export const checkIsTerminalPayment = (pType) =>
  pType === PAYMENT_TYPES_NAME.Terminal;

export const getSubscriberPayload = (
  values,
  isResponsible,
  responsibleParty,
  paymentId,
  paymentType,
  additionMembers,
  addonsList,
  draftSubscription = false,
  countryConfig = {},
) => {
  const {
    partner_id,
    group_id,
    region_id,
    location_id,
    patient_name,
    date_of_birth,
    gender,
    biological_sex,
    pms_id,
    pms_display_id,
    facility_id = null,
    start_date,
  } = values;

  const externalPayment = checkIfExternalPayment(paymentType);

  const primaryMember = {
    plans: [
      {
        plan_id: values?.plan_id,
        include_signup: values?.include_signup,
      },
    ],
    patient_name,
    date_of_birth: formatToDateString(date_of_birth),
    gender,
  };

  if (countryConfig?.biologicalSexRequired) {
    primaryMember['biological_sex'] = biological_sex || gender;
  }

  if (pms_id) {
    primaryMember['pms_id'] = pms_id;
  }
  if (pms_display_id) {
    primaryMember['pms_display_id'] = pms_display_id;
  }

  let data = {
    member_type: SUBSCRIPTION_TYPE.CUSTOMER.value,
    partner_id,
    group_id,
    location_id,
    region_id,
    facility_id,
    members: [primaryMember],
  };

  if (start_date && !moment(start_date).isSame(moment(), 'day')) {
    data.start_date = formatToMilliSeconds(start_date);
  }

  if (draftSubscription) {
    data.is_draft = true;
  } else if (externalPayment || paymentId) {
    const isTerminalPayment = checkIsTerminalPayment(paymentType);
    data.payment_type = isTerminalPayment
      ? PAYMENT_TYPES_NAME.Card
      : paymentType;

    if (paymentId && isTerminalPayment) {
      data.payment_method_state = 'terminal';
      data.payment_intent_id = paymentId;
    } else {
      data.payment_method_id = paymentId;
    }
  }

  if (values?.addons?.length) {
    values?.addons.forEach((indAddon) => {
      const planDetail = Utils.getPlanId(
        indAddon,
        'Primary',
        values?.plan_frequency,
        addonsList,
      );
      const { plan_id } = planDetail || {};

      plan_id &&
        data?.members?.[0]?.plans?.push({
          plan_id,
          include_signup: values?.addons_signup?.[indAddon],
        });
    });
  }

  if (isResponsible) {
    data = {
      ...data,
      responsible_member: {
        ...responsibleParty,
        date_of_birth: formatToDateString(responsibleParty?.date_of_birth),
      },
    };
  } else {
    data = {
      ...data,
      phone: values?.phone,
      email: values?.email,
      address: { ...values?.address, country_code: countryConfig?.code },
    };
  }

  const dependentsFinal = prepareDependentsPayload(
    additionMembers,
    values?.plan_frequency,
    addonsList,
    countryConfig,
  );
  data.members = [...data.members, ...dependentsFinal];

  return data;
};

const buildEmployeeSelfResponsibleMember = (values, countryConfig = {}) => {
  const fn = values?.patient_name?.first_name?.trim() || '';
  const ln = values?.patient_name?.last_name?.trim() || '';
  const full_name = [fn, ln].filter(Boolean).join(' ');

  return {
    full_name,
    relationship: 'Self',
    email: values?.email,
    phone: values?.phone,
    date_of_birth: formatToDateString(values?.date_of_birth),
    address: {
      ...values?.address,
      country_code: countryConfig?.code,
    },
  };
};

const getCreateEmployeePayload = (
  values,
  isResponsible,
  responsibleParty,
  paymentId,
  paymentType,
  additionMembers,
  addonsList,
  draftSubscription = false,
  countryConfig = {},
) => {
  let payload = getSubscriberPayload(
    values,
    isResponsible,
    responsibleParty,
    paymentId,
    paymentType,
    additionMembers,
    addonsList,
    draftSubscription,
    countryConfig,
  );
  payload.member_type = SUBSCRIPTION_TYPE.EMPLOYEE.value;

  if (!isResponsible) {
    payload.responsible_member = buildEmployeeSelfResponsibleMember(
      values,
      countryConfig,
    );
  }

  return payload;
};

// subscriber config end

// users

const getRoleList = () => Object.values(Roles);

const filterPlanListMY = (planList) => {
  const plans = _.cloneDeep(planList);
  const finalPlans = [];
  const stripeKeys = [];

  plans.forEach((plan) => {
    const { billing_frequency, stripe_product_id } = plan;
    const index = _.indexOf(stripeKeys, stripe_product_id);

    if (index > -1) {
      finalPlans[index] = {
        ...finalPlans[index],
        [billing_frequency]: plan,
      };
    } else {
      stripeKeys.push(stripe_product_id);

      finalPlans.push({ [billing_frequency]: plan });
    }
  });

  return finalPlans;
};

export const checkIfZeroDollarPlan = (plan) => {
  return plan?.pricing_tiers_list?.every((pricingTier) =>
    pricingTier?.pricing_tiers?.tiers?.every(
      (tier) => _.isEmpty(tier) || tier?.unit_amount === 0,
    ),
  );
};

export const formatPhoneNumber = (phoneNumberString, countryConfig) => {
  const { code = 'US' } = countryConfig || {};
  const cleaned = ('' + phoneNumberString)?.replace(/\D/g, '');

  // Handle GB (UK) phone number formatting
  if (code === 'GB') {
    let ukNumber = cleaned;

    // Remove '44' prefix if present (UK country code)
    if (ukNumber.startsWith('44')) {
      ukNumber = ukNumber.slice(2);
    }

    // Format based on UK number patterns (10 digits after removing country code)
    if (ukNumber.length === 10) {
      // Mobile numbers (7xxx xxxxxx or any 10-digit pattern)
      if (ukNumber.startsWith('7')) {
        // Mobile: 7xxx xxxxxx
        const match = ukNumber.match(/^(7\d{3})(\d{6})$/);
        if (match) {
          return `${match[1]} ${match[2]}`;
        }
      } else if (ukNumber.startsWith('20')) {
        // London: 20 xxxx xxxx
        const match = ukNumber.match(/^(20)(\d{4})(\d{4})$/);
        if (match) {
          return `${match[1]} ${match[2]} ${match[3]}`;
        }
      } else if (ukNumber.startsWith('1')) {
        // Other landlines: 1xxx xxxxxx
        const match = ukNumber.match(/^(1\d{3})(\d{6})$/);
        if (match) {
          return `${match[1]} ${match[2]}`;
        }
        // Alternative pattern: 1xx xxx xxxx
        const match2 = ukNumber.match(/^(1\d{2})(\d{3})(\d{4})$/);
        if (match2) {
          return `${match2[1]} ${match2[2]} ${match2[3]}`;
        }
      } else {
        // Generic 10-digit pattern: xxxx xxxxxx
        const match = ukNumber.match(/^(\d{4})(\d{6})$/);
        if (match) {
          return `${match[1]} ${match[2]}`;
        }
      }
    } else if (ukNumber.length === 9) {
      // Some shorter landline patterns
      if (ukNumber.startsWith('1')) {
        const match = ukNumber.match(/^(1\d{2})(\d{3})(\d{3})$/);
        if (match) {
          return `${match[1]} ${match[2]} ${match[3]}`;
        }
      } else {
        // Generic 9-digit pattern: xxx xxx xxx
        const match = ukNumber.match(/^(\d{3})(\d{3})(\d{3})$/);
        if (match) {
          return `${match[1]} ${match[2]} ${match[3]}`;
        }
      }
    } else {
      // For any other length, try to format as xxxx xxxxxx if it has enough digits
      const match = ukNumber.match(/^(\d{4})(\d{6})$/);
      if (match) {
        return `${match[1]} ${match[2]}`;
      }
    }

    // Return as-is if no pattern matches (for partial input)
    return ukNumber || null;
  }

  // Handle US phone number formatting (existing logic)
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return ['(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return null;
};

export const getPhoneNumberWithCountryCode = (phoneNumberString) => {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = /^(1|)?(\d{3})(\d{3})(\d{4})$/.exec(cleaned);

  if (match) {
    return [1, match[2], match[3], match[4]].join('');
  }

  return null;
};

export const formatTaxId = (value) => {
  const cleaned = ('' + value).replace(/\D/g, '');

  return cleaned.slice(0, 9);
};

const checkIfSuccess = (res) => Boolean(res?.type === 'success');

const checkIfPresent = (attri1, attri2) => Boolean(attri1 && attri2);

const ternaryCheck = (condition, value, defaultValue) =>
  condition ? value : defaultValue;

const getValue = (value, defaultValue) => value || defaultValue;

const getRole = (role) => {
  let roleKey = role;
  if (role?.includes(':')) {
    roleKey = role.split(':')[1];
  }

  switch (roleKey) {
    case Roles.PARTNER_ADMIN.key:
      return Roles.PARTNER_ADMIN.value;
    case Roles.PARTNER_USER.key:
      return Roles.PARTNER_USER.value;
    case Roles.GROUP_ADMIN.key:
      return Roles.GROUP_ADMIN.value;
    case Roles.GROUP_USER.key:
      return Roles.GROUP_USER.value;
    case Roles.REGION_ADMIN.key:
      return Roles.REGION_ADMIN.value;
    case Roles.REGION_USER.key:
      return Roles.REGION_USER.value;
    case Roles.SUPER_ADMIN.key:
      return Roles.SUPER_ADMIN.value;
    case Roles.SUBSCRIBILI_USER.key:
      return Roles.SUBSCRIBILI_USER.value;
    case Roles.LOCATION_ADMIN.key:
      return Roles.LOCATION_ADMIN.value;
    case Roles.LOCAL_USER.key:
      return Roles.LOCAL_USER.value;
    default:
      return 'Unknown';
  }
};

const formatUserRole = (role) => {
  if (!role) {
    return '';
  }

  if (role.indexOf('r:') !== -1) {
    let splitRole = role.split(':');
    return ROLES_LIST[splitRole[1]] || splitRole[1];
  }

  return ROLES_LIST[role] || role;
};

export const getPhone = (phone) => {
  if (phone[0] === '1') {
    return phone.substring(1, phone.length);
  }
  return phone;
};

// This parser is used from libphonenumber-js :
export const extractOnlyPhoneNumber = (phoneNumberWithCountryCode) => {
  if (phoneNumberWithCountryCode) {
    const phoneNumber = parsePhoneNumber(
      `+${phoneNumberWithCountryCode}`,
    )?.nationalNumber;
    return phoneNumber ?? '';
  }
  return '';
};

export const checkIfInternalUser = (user) => {
  const rolesList = [SUPER_ADMIN, SUBSCRIBILI_USER];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const isSubscribiliUser = (user) => {
  const subscribiliRole = SUBSCRIBILI_USER;
  return user?.['custom:role'].includes(subscribiliRole.key);
};

export const isSuperadminUser = (user) => {
  const superadminRole = SUPER_ADMIN;
  return user?.['custom:role'].includes(superadminRole.key);
};

export const isRevshareAdmin = (user) => {
  const rolesList = [REVSHARE_ADMIN];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const isPartnerUser = (user) => {
  const rolesList = [PARTNER_ADMIN, PARTNER_USER];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const isGroupUser = (user) => {
  const rolesList = [GROUP_ADMIN, GROUP_USER];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const isRegionUser = (user) => {
  const rolesList = [REGION_ADMIN, REGION_USER];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const isLocationUser = (user) => {
  const rolesList = [LOCATION_ADMIN, LOCAL_USER];

  return rolesList?.some((role) => user?.['custom:role']?.includes(role.key));
};

export const getLocationOption = (user, hasFullAccess) => ({
  location_name: isLocationUser(user) && !hasFullAccess ? 'My Locations' : 'All Locations',
  location_id: 'ALL_LOCATIONS',
  group_id: 'ALL_GROUPS',
});

export const getRegionOption = (user, hasFullAccess) => ({
  region_name: isRegionUser(user) && !hasFullAccess ? 'My Regions' : 'All Regions',
  region_id: 'ALL_REGIONS',
  group_id: 'ALL_GROUPS',
});

export const calculatePercentageDiscount = (fees, fixedPercent) => {
  const discountRate = fixedPercent / 100;
  const totalAmount = fees + fees * discountRate;

  return parseFloat(totalAmount.toFixed(2));
};

export const calculateDiscount = (
  finalAmount = 0,
  processing_charges,
  annualCost,
  couponDetail,
  skipFixedValue = false,
  cardType,
  hasExistingPayment = false,
) => {
  let fixedPercent = REACT_APP_STRIPE_CHARGE_PERCENT;
  let fixedCents = 0;
  let discountAmount = 0;

  if (!cardType) {
    fixedPercent = REACT_APP_STRIPE_CHARGE_PERCENT;
    fixedCents = REACT_APP_CREDIT_CHARGE_FIXED_CENTS;
  } else if (cardType === 'credit') {
    fixedPercent = REACT_APP_CREDIT_CHARGE_PERCENT;
    fixedCents = REACT_APP_CREDIT_CHARGE_FIXED_CENTS;
  } else {
    fixedPercent = REACT_APP_DEBIT_CHARGE_PERCENT;
    fixedCents = REACT_APP_DEBIT_CHARGE_FIXED_CENTS;
  }

  if (finalAmount) {
    if (couponDetail?.percent_off) {
      discountAmount = (finalAmount * couponDetail.percent_off) / 100;
    } else if (couponDetail?.amount_off) {
      discountAmount = parseFloat(couponDetail.amount_off / 100);
    }
  }

  if (finalAmount < 0) {
    processing_charges = 0;
    discountAmount = 0;
  }

  if (finalAmount > 0) {
    if (discountAmount > finalAmount) {
      discountAmount = finalAmount;
      processing_charges = 0;
    }
  }
  let costannual = discountAmount + finalAmount;

  let finalizedAmt = finalAmount - discountAmount;
  let dynamicFee = (finalizedAmt && finalizedAmt + fixedCents / 100) || 0;
  const dynamicProcessingCharges =
    calculatePercentageDiscount(dynamicFee, fixedPercent) - finalizedAmt;
  const customProcessingCharges =
    cardType || hasExistingPayment ? dynamicProcessingCharges : 0;
  const processingCharges = processing_charges ? customProcessingCharges : 0;

  return {
    discount: discountAmount,
    amountDue: finalAmount,
    processingFee: processingCharges,
    annualCost: costannual,
  };
};

export const isDentalIndustry = (industry) =>
  DENTAL_INDUSTRIES.includes(industry);

export const getPlanPackageType = (selPlan) => {
  if (selPlan?.pricing_tiers_list?.[0]?.plan_type === 'Tiered') return 'Tiered';

  return selPlan?.plan_package_type || 'Base';
};

export const getPlanType = (plan_package_id, planArray = []) => {
  const selPlan =
    planArray?.find((item) => item.plan_package_id === plan_package_id) || {};

  return selPlan?.pricing_tiers_list?.[0]?.plan_type || '';
};

export const getPlanId = (...args) => {
  const [plan_package_id, , , , isTieredPlan = false] = args;

  if (!plan_package_id) return;

  if (isTieredPlan) {
    const planDetails = getTieredPlanId(...args);

    return planDetails;
  } else {
    const planDetails = getBasePlanId(...args);

    return planDetails;
  }
};

const getBasePlanId = (plan_package_id, memberType, frequency, planArray) => {
  const selPlan = planArray.find(
    (item) => item.plan_package_id === plan_package_id,
  );
  const tiers = selPlan?.pricing_tiers_list.filter(
    (item) =>
      item?.pricing_tiers?.billing_frequency ===
      PLAN_PROCESS_FREQUENCIES[frequency],
  );
  const general = tiers?.filter((item) => item.plan_type === 'General') || [];

  if (general.length) {
    return {
      plan_name: selPlan?.plan_name,
      plan_type: 'General',
      plan_id: general?.[0]?.plan_id,
      selectedTier: general?.[0]?.pricing_tiers,
    };
  } else {
    const selPriceTier =
      tiers?.find((item) => item.plan_type === memberType) || {};
    return {
      plan_name: selPlan?.plan_name,
      plan_type: memberType,
      plan_id: selPriceTier?.plan_id,
      selectedTier: selPriceTier?.pricing_tiers,
    };
  }
};

export const getTieredPlanId = (
  plan_package_id,
  _memberType,
  frequency,
  planArray,
  _isTieredPlan,
  memberId,
) => {
  if (!plan_package_id) return;

  const selPlan = planArray.find(
    (item) => item.plan_package_id === plan_package_id,
  );
  const tiers = selPlan?.pricing_tiers_list.filter(
    (item) =>
      item?.pricing_tiers?.billing_frequency ===
      PLAN_PROCESS_FREQUENCIES[frequency],
  );

  const selPriceTier = tiers?.find(
    (item) =>
      memberId >= Number(item?.member_from) && memberId <= item?.member_to,
  );

  return {
    plan_name: selPlan?.plan_name,
    plan_type: 'Tiered',
    plan_id: selPriceTier?.plan_id,
    selectedTier: selPriceTier?.pricing_tiers,
  };
};

export const showBookAppointments = (pmsVendor = []) => {
  const vendorsList = ['athena', 'elation health', 'relay'];

  return !pmsVendor.some((vendor) => vendorsList.includes(vendor));
};

export const PMS_SYNC_PLAN_VENDORS = new Set(['carestack', 'opendental']);

const getPrimaryPmsVendor = (settings = {}) =>
  String(settings?.pms_vendor?.[0] || '').toLowerCase();

export const isPmsVendor = (settings = {}, vendorName = '') =>
  Boolean(vendorName) &&
  getPrimaryPmsVendor(settings) === String(vendorName).toLowerCase();

export const isOpenDentalVendor = (settings = {}) =>
  isPmsVendor(settings, 'opendental');

export const isPmsPlanSyncEnabled = (settings = {}) => {
  const { capture_patient_id = false } = settings;
  return (
    Boolean(capture_patient_id) &&
    PMS_SYNC_PLAN_VENDORS.has(getPrimaryPmsVendor(settings))
  );
};

export const calculateDynamicMemberCharges = (
  selectedPriceTier = {},
  includeSignup = true,
  zeroProcessingFee = false,
  cardType = 'credit',
) => {
  const { tiers = [] } = selectedPriceTier;
  const {
    unit_amount = 0,
    upfront_amount = 0,
    signup_amount = 0,
  } = tiers[0] || {};
  let fixedCents = 0;

  if (cardType === 'credit') {
    fixedCents = REACT_APP_CREDIT_CHARGE_FIXED_CENTS;
  } else if (cardType === 'debit' || cardType === 'prepaid') {
    fixedCents = REACT_APP_DEBIT_CHARGE_FIXED_CENTS;
  } else {
    fixedCents = REACT_APP_STRIPE_CHARGE_FIXED_CENTS;
  }

  // Primary Member Charges
  const primary_mem_amount = unit_amount / 100;
  const primary_up_fees = upfront_amount / 100;
  let primary_signup_fees = 0;

  if (includeSignup) {
    primary_signup_fees = signup_amount / 100;
  }

  // Primary Member Processing Fees
  const primary_recurring_pro_fee = primary_mem_amount;
  const primary_upfront_pro_fee = primary_up_fees;
  const primary_signup_pro_fee = primary_signup_fees;
  const totalProcessingFee = zeroProcessingFee
    ? 0
    : primary_recurring_pro_fee +
    primary_upfront_pro_fee +
    primary_signup_pro_fee +
    parseInt(fixedCents) / 100;
  const primaryMemberCost =
    primary_mem_amount + primary_up_fees + primary_signup_fees;

  return {
    primary_mem_amount,
    primary_up_fees,
    primary_signup_fees,
    totalProcessingFee,
    primaryMemberCost,
  };
};

export const doesPlanHasActivationCost = (selectedPriceTier = {}) => {
  const { tiers = [] } = selectedPriceTier;
  const { signup_amount = 0 } = tiers[0] || {};

  return signup_amount > 0;
};

export const getDecimalValue = (value) => {
  let replacedValue = value.replace(/[^0-9.]/g, '').replace(/\.{2,}/g, '.');
  let lastIndex;

  // Replace multiple dots
  while (
    replacedValue.indexOf('.') !== (lastIndex = replacedValue.lastIndexOf('.'))
  ) {
    replacedValue =
      replacedValue.substring(0, lastIndex) +
      replacedValue.substring(lastIndex + 1);
  }

  return replacedValue;
};

export const processPlanByFrequency = (plans = []) => {
  const groupedPlans = {};
  const getFrequencyKey = (selFreq) =>
    Object.keys(PLAN_PROCESS_FREQUENCIES).find(
      (key) => PLAN_PROCESS_FREQUENCIES[key] === selFreq,
    );

  plans.length &&
    plans.forEach((plan) => {
      plan?.pricing_tiers_list?.forEach((pTier) => {
        const plan_frequency = getFrequencyKey(
          pTier?.pricing_tiers?.billing_frequency,
        );
        const planExists =
          groupedPlans?.[plan_frequency]?.length &&
          groupedPlans[plan_frequency].find(
            (gPlan) => gPlan.plan_package_id === plan.plan_package_id,
          );

        if (!planExists) {
          !groupedPlans?.[plan_frequency] &&
            (groupedPlans[plan_frequency] = []);
          groupedPlans[plan_frequency].push(plan);
        }
      });
    });

  return groupedPlans;
};

/**
 * Drops archived *plans* (entries in `pricing_tiers_list`, each with its own `status`).
 * Packages whose tiers are all archived are removed entirely.
 */
export const excludeArchivedPlansFromPlanPackages = (rows = []) =>
  rows
    .map((pkg) => ({
      ...pkg,
      pricing_tiers_list: pkg?.pricing_tiers_list?.filter(
        (tier) => tier?.status !== 'Archived',
      ),
    }))
    .filter((pkg) => (pkg.pricing_tiers_list?.length ?? 0) > 0);

const processHiddenPlans = (allPlans, targetBillingFrequency) => {
  const uniquePlanIds = new Set();
  const uniquePlans = [];

  allPlans?.forEach((plan) => {
    plan?.pricing_tiers_list?.forEach((pricingTier) => {
      const currentBillingFrequency =
        pricingTier?.pricing_tiers?.billing_frequency;

      if (currentBillingFrequency === targetBillingFrequency) {
        const planStatus =
          pricingTier?.status === 'Active' ? 'Active' : 'Hidden';
        const planId = plan.plan_package_id;

        if (!uniquePlanIds.has(planId)) {
          uniquePlanIds.add(planId);
          uniquePlans.push({ ...plan, status: planStatus });
        }
      }
    });
  });

  return uniquePlans;
};

/**
 * Filters plans to only include those that does not allow legacy reactivation.
 * It iterates through plans grouped by frequency and filters out plan packages
 * that has the `legacy_reactivation_allowed` flag set to true in any of their pricing tiers.
 * @param {object} plans - An object containing plans grouped by billing frequency.
 * @returns {object} A new object with the same structure as the input, but with only non legacy-reactivation-allowed plans.
 */
const filterLegacyPlans = (plans) => {
  const filteredPlans = {};

  for (const frequency in plans) {
    if (Object.hasOwn(plans, frequency)) {
      const planPackages = plans[frequency];

      if (Array.isArray(planPackages)) {
        filteredPlans[frequency] = planPackages.filter((planPackage) =>
          planPackage.pricing_tiers_list.some(
            (tier) => tier.legacy_reactivation_allowed !== true,
          ),
        );
      }
    }
  }

  return filteredPlans;
};

const doesStripeFeesPaidByProvider = (planList, planId) => {
  const pricingTiersList =
    planList.map((plans) => plans?.pricing_tiers_list).flat(1) || [];
  let selectedSubscriberPlan = pricingTiersList?.find(
    (plan) => plan?.plan_id == planId,
  );
  const { stripe_fee_paid_by = '' } =
    selectedSubscriberPlan?.pricing_tiers || {};

  return Boolean(stripe_fee_paid_by === 'provider');
};

export const getPaymentDisplay = (paymentList = [], name = '') => {
  const { display = name } =
    paymentList.find((indPayment) => indPayment?.name === name) || {};

  return display;
};

export const getEffectiveProviderPaymentTypes = (locationSettingsData = {}) => {
  const { settings = {}, group_settings = {} } = locationSettingsData;

  return (
    settings.provider_payment_types ??
    group_settings.provider_payment_types ??
    []
  );
};

const checkForSpellCorrection = (spellCorrectedFlag, spellCorrected) =>
  spellCorrectedFlag || spellCorrected || false;
const checkForReplacedComponents = (replacedFlag, replaced) =>
  replacedFlag || replaced || false;
const checkForUnexpected = (
  unexpectedFlag,
  unexpected,
  confirmationLevelInfo,
) => {
  const confirmationLevel = ['CONFIRMED'];
  return unexpected || confirmationLevel.includes(confirmationLevelInfo)
    ? false
    : unexpectedFlag || false;
};

const getAddressFields = (addressComponents) => {
  const requiredAddressFields = [
    'street_number', //line 1
    'route', // line 1
    'neighborhood', //line 2
    'locality', // city
    'administrative_area_level_1', // state
    'postal_code', // zipCode
  ];
  let results = { line1: '', line2: '', city: '', state: '', zipcode: '' };
  let spellCorrected = false;
  let unexpected = false;
  let replaced = false;
  addressComponents.forEach((elem) => {
    const key = elem?.types?.[0] || elem?.componentType || '';
    if (requiredAddressFields.includes(key)) {
      switch (key) {
        case 'street_number':
          results = {
            ...results,
            line1: elem?.long_name || elem?.componentName?.text || '',
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        case 'route':
          results = {
            ...results,
            line1:
              results?.line1 +
              ` ${elem?.long_name || elem?.componentName?.text || ''}`,
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        case 'locality':
          results = {
            ...results,
            city: elem?.long_name || elem?.componentName?.text || '',
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        case 'administrative_area_level_1':
          results = {
            ...results,
            state: elem?.short_name || elem?.componentName?.text || '',
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        case 'neighborhood':
          results = {
            ...results,
            line2: elem?.long_name || elem?.componentName?.text || '',
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        case 'postal_code':
          results = {
            ...results,
            zipcode: elem?.long_name || elem?.componentName?.text || '',
          };
          spellCorrected = checkForSpellCorrection(
            elem?.spellCorrected,
            spellCorrected,
          );
          replaced = checkForReplacedComponents(elem?.replaced, replaced);
          unexpected = checkForUnexpected(
            elem?.unexpected,
            unexpected,
            elem?.confirmationLevel,
          );
          break;
        default:
          results = { ...results };
          spellCorrected = false;
          unexpected = false;
          replaced = false;
      }
    }
  });
  return { results, spellCorrected, unexpected, replaced };
};

const checkAddressValidity = (
  validityVerdict,
  spellCorrected = false,
  unexpected = false,
  replaced = false,
  uspsData = {},
) => {
  let dpvConfirmation = uspsData?.dpvConfirmation || 'N';
  const acceptedValidationGranularity = ['PREMISE', 'SUB_PREMISE'];
  const validationGranularity =
    validityVerdict?.validationGranularity || 'OTHERS';
  let verdict;
  if (spellCorrected || unexpected || replaced)
    return { isValidAddress: false, validationGranularity };

  if (acceptedValidationGranularity.includes(validationGranularity))
    verdict = { isValidAddress: true, validationGranularity };
  else {
    switch (dpvConfirmation) {
      case '':
      case 'N':
      case 'D':
        verdict = { isValidAddress: false, validationGranularity };
        break;
      case 'S':
      case 'Y':
        verdict = { isValidAddress: true, validationGranularity };
        break;

      default:
        verdict = { isValidAddress: false, validationGranularity };
    }
  }
  return verdict;
};

const getSortedPricingTiers = (pricing_tiers_list) => {
  pricing_tiers_list.sort((a, b) => {
    if (
      a?.pricing_tiers?.billing_frequency ===
      b?.pricing_tiers?.billing_frequency
    ) {
      return a.member_from - b.member_from;
    }

    return a?.pricing_tiers?.billing_frequency.localeCompare(
      b?.pricing_tiers?.billing_frequency,
    );
  });

  return pricing_tiers_list;
};

const getStripeError = (errorData) => {
  let message;
  switch (errorData?.decline_code || errorData?.code) {
    case STRIPE_ERRORS.CHARGE_DECLINED:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED;
      break;
    case STRIPE_ERRORS.CHARGE_DECLINED_INSUFFICIENT_FUNDS:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED_INSUFFICIENT_FUNDS;
      break;
    case STRIPE_ERRORS.CHARGE_DECLINED_LOST_CARD:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED_LOST_CARD;
      break;
    case STRIPE_ERRORS.CHARGE_DECLINED_EXPIRED_CARD:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED_EXPIRED_CARD;
      break;
    case STRIPE_ERRORS.CHARGE_DECLINED_STOLEN_CARD:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED_STOLEN_CARD;
      break;
    case STRIPE_ERRORS.CHARGE_DECLINED_PROCESSING_ERROR:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED_PROCESSING_ERROR;
      break;
    default:
      message = STRIPE_ERRORS_MESSAGES.CHARGE_DECLINED;
      break;
  }

  return message;
};

/**
 * Filters the given list of plans, dropping only Tiered plans that have no
 * active primary cost. Non-Tiered plans (General/Primary/Dependent) are always
 * kept, since they legitimately have no member_from/member_to tier.
 *
 * A Tiered plan is considered to have a primary cost if it has a pricing tier
 * where `member_from` and `member_to` are both 1, and the status is 'Active'.
 *
 * @param {Array} plansList - The list of plans to filter.
 * @param {string} selectedFrequency - The selected frequency (not used in the current implementation).
 * @returns {Array} - The filtered list of plans.
 */
export const getPlansWithPrimaryCost = (plansList, selectedFrequency) => {
  const planList =
    plansList?.filter((plan) => {
      const isTieredPlan =
        plan?.pricing_tiers_list?.[0]?.plan_type === 'Tiered';
      if (!isTieredPlan) {
        return true;
      }
      return plan?.pricing_tiers_list?.some(
        (tiersList) =>
          tiersList?.member_from === 1 &&
          tiersList?.member_to === 1 &&
          tiersList?.status === 'Active',
      );
    }) || [];
  return planList;
};

/**
 * Filters and returns the active frequency tiers from the provided pricing tiers list.
 *
 * @param {Array} [pricingTiersList=[]] - The list of pricing tiers.
 * @returns {Array} - The filtered list of active frequency tiers.
 */
export const getActiveBillingFrequencyTiers = (pricingTiersList = []) => {
  const isTieredPlan = pricingTiersList?.[0]?.plan_type === 'Tiered';

  const activeBillingFrequencyTiers =
    pricingTiersList?.filter(
      (currentTier, index, tiersArray) =>
        index ===
        tiersArray.findIndex((tier) => {
          const hasPrimaryCost = isTieredPlan
            ? tier?.member_from === 1 &&
            tier?.member_to === 1 &&
            tier?.status === 'Active'
            : true;

          return (
            tier.pricing_tiers?.billing_frequency ===
            currentTier.pricing_tiers?.billing_frequency && hasPrimaryCost
          );
        }),
    ) || [];

  return activeBillingFrequencyTiers;
};

export const dollarsToCents = (dollars) => {
  return parseInt((dollars * 100).toFixed(0));
};

export const calculateTerminalProcessingFees = (subTotal) => {
  const value = (subTotal * REACT_APP_CREDIT_CHARGE_PERCENT) / 100;

  return dollarsToCents(value);
};

export const getTerminalDisclaimerMsg = (finalDueAmount) => {
  // finalDue amount is in dollars
  const finalDisclaimerAmount =
    finalDueAmount +
    centsToDollars(calculateTerminalProcessingFees(finalDueAmount));

  return `Credit card payments incur a ${REACT_APP_CREDIT_CHARGE_PERCENT}% processing fee, totaling $${finalDisclaimerAmount?.toFixed(
    2,
  )} based on your current total`;
};

export const getCountryConfig = (country_code) => {
  if (!country_code) return {};
  if (country_code === 'US') {
    return US_CONFIG;
  } else if (country_code === 'GB') {
    return GB_CONFIG;
  }
};

export const formatPhoneWithCountryCode = (
  phone,
  countryConfig,
  removeCountryCode = false,
) => {
  if (!phone) return '';

  const phoneStr = String(phone).trim();
  const countryCode = countryConfig?.phone?.countryCode || '';

  // Remove the '+' from country code to get just the number (e.g., '1' or '44')
  const countryCodeNumber = countryCode.replace('+', '');

  // Remove '+' if phone starts with it, then remove all non-digits
  let cleanedPhone = phoneStr.startsWith('+')
    ? phoneStr.substring(1).replaceAll(/\D/g, '')
    : phoneStr.replaceAll(/\D/g, '');

  // Remove leading 0 after country code if present
  if (cleanedPhone.startsWith(countryCodeNumber) && countryConfig?.code === 'GB') {
    const afterCountryCode = cleanedPhone.substring(countryCodeNumber.length);
    // If the first digit after country code is 0, remove it
    if (afterCountryCode && afterCountryCode.length > 10 && afterCountryCode.startsWith('0')) {
      cleanedPhone = countryCodeNumber + afterCountryCode.substring(1);
    }
  }

  // If removeCountryCode is true, strip the country code from the phone number
  if (removeCountryCode && countryConfig?.code !== 'US') {
    if (cleanedPhone.startsWith(countryCodeNumber)) {
      return cleanedPhone.substring(countryCodeNumber.length);
    }
    return cleanedPhone;
  }

  // If phone already starts with the country code number (e.g., '1' or '44'), return as-is
  if (cleanedPhone.startsWith(countryCodeNumber)) {
    return cleanedPhone;
  }

  // Otherwise, add the country code number (without '+')
  return `${countryCodeNumber}${cleanedPhone}`;
};

/**
 * Get the billing full name label based on payment type
 * @param {string} paymentType - The payment type (Card, BACS_Debit, etc.)
 * @returns {string} The appropriate label for the full name field
 */
export const getBillingFullNameLabel = (paymentType) => {
  if (paymentType === PAYMENT_TYPES_NAME.BACS_Debit) {
    return 'Bank Account Holder Name';
  }
  return 'Card holder Name';
};

export const shouldSkipSentryLogging = (error) => {
  const status = error?.status || error?.response?.status;
  return [400, 401].includes(status);
};

const Utils = {
  validateEmail,
  validateMobile,
  validateZipCode,
  validateWebsite,
  validateFourDigitSSN,
  validateNPI,
  validateTaxId,
  validateFacilityId,
  validateSlug,
  validateName,
  isValidDate,
  getQueryString,
  getKeysName,
  checkAllLevels,
  membersConfig,
  getPartnerId,
  defaultFieldState,
  getEmployeeReqFields,
  getSubscriberReqFields,
  responsibleReqFields,
  subscriberValues,
  employeeValues,
  responsibleValues,
  filterPlanList,
  getFinalPlanList,
  _calculateAge,
  getDateError,
  getStripeDateError,
  getTargetValue,
  checkIfExternalPayment,
  getSubscriberPayload,
  getCreateEmployeePayload,
  getRoleList,
  formatPhoneNumber,
  filterPlanListMY,
  checkIfSuccess,
  checkIfPresent,
  ternaryCheck,
  getValue,
  getRole,
  formatUserRole,
  getPhone,
  checkIfInternalUser,
  calculateDiscount,
  getPlanType,
  isDentalIndustry,
  getPlanPackageType,
  getPlanId,
  showBookAppointments,
  isPmsPlanSyncEnabled,
  isPmsVendor,
  isOpenDentalVendor,
  isSubscribiliUser,
  isSuperadminUser,
  isPartnerUser,
  isRevshareAdmin,
  isGroupUser,
  isRegionUser,
  isLocationUser,
  doesPlanHasActivationCost,
  processPlanByFrequency,
  excludeArchivedPlansFromPlanPackages,
  processHiddenPlans,
  filterLegacyPlans,
  doesStripeFeesPaidByProvider,
  getAddressFields,
  checkAddressValidity,
  addressFieldsBlurred,
  getSortedPricingTiers,
  getStripeError,
  getPlansWithPrimaryCost,
  getCountryConfig,
  formatPhoneWithCountryCode,
  getBillingFullNameLabel,
  shouldSkipSentryLogging
};

export default Utils;
